import type { ReploErrorConfig } from "schemas/errors";

import { IframeRpcChannelError, ReploError } from "schemas/errors";
import { z } from "zod";

export class BedrockConfigError extends ReploError {}

/** An error whose text was written for the user, so toasts and inline failures show it over the generic title. */
export abstract class UserFacingError extends ReploError {
  readonly description: string | null;

  constructor(
    config: ReploErrorConfig & { message: string; description: string | null },
  ) {
    super(config);
    this.description = config.description;
  }
}

/**
 * Thrown by a browser-auth `getToken` once it has already started sending the
 * tab back to login. Every in-flight query rejects with it at the same moment,
 * and none of them should toast: the redirect is the whole user-facing outcome.
 */
export class BedrockSessionInvalidatedError extends ReploError {}

// A coordinator RPC the server rejected; its message is internal plumbing, never shown verbatim.
export const REALTIME_RPC_REJECTED_CODE = "REALTIME_RPC_REJECTED";

export class BedrockApiRequestError extends Error {
  code: string;
  status: number;
  // (Cole, 2026-07-08, REPL-28948): named for extractErrorId(); absent = not a Replo error response.
  reploErrorId?: string;

  constructor({
    code,
    status,
    message,
    reploErrorId,
  }: {
    code: string;
    status: number;
    message: string;
    reploErrorId?: string;
  }) {
    super(message);
    this.code = code;
    this.status = status;
    this.reploErrorId = reploErrorId;
  }
}

/**
 * Toast payload for a failed mini-app query or mutation. `id` lets callers
 * collapse a burst of identical failures into one toast: when the session
 * handshake breaks, every in-flight request rejects at once, and sonner reuses
 * a toast when the same id is passed.
 */
export interface MiniappErrorToastContent {
  title: string;
  description?: string;
  id?: string;
  cause?: {
    errorId?: string;
    errorCode?: string;
    errorName?: string;
    errorMessage?: string;
  };
}

const GENERIC_MINIAPP_ERROR_TITLE = "Something went wrong";

/**
 * True when the failure is minting the mini-app's session token, which is
 * relayed through the parent editor's `requestAuth` RPC. Its raw message
 * ("RPC method 'requestAuth' failed: Airlock request failed") is internal
 * plumbing that users were seeing verbatim (REPL-31464).
 */
function isMiniappAuthTokenError(error: unknown): boolean {
  return (
    error instanceof IframeRpcChannelError &&
    error.config.additionalData?.method === "requestAuth"
  );
}

/**
 * Maps an unknown query/mutation error to user-facing toast content for the
 * shared mini-app error handlers. The only messages we surface verbatim are a
 * `BedrockApiRequestError`, which bedrock-api writes to be user-facing, and a
 * `UserFacingError`; every other error — internal iframe-RPC transport
 * failures, and any stray JS error — is shown as a generic message so no
 * raw/internal string reaches the user.
 */
export function getMiniappErrorToastContent(
  error: unknown,
): MiniappErrorToastContent {
  if (isMiniappAuthTokenError(error)) {
    return {
      title: "Couldn't load this app",
      description:
        "Give it a moment and try again. If it keeps happening, reload the page.",
      id: "miniapp-auth-token-error",
    };
  }
  // Any other iframe RPC failure (timeout, unknown method, invalid input) is
  // internal detail the user can't act on, so never surface it verbatim. Share
  // one id so a burst of these collapses into a single toast.
  if (error instanceof IframeRpcChannelError) {
    return { title: GENERIC_MINIAPP_ERROR_TITLE, id: "miniapp-rpc-error" };
  }
  // A sandbox outage rejects every in-flight RPC at once, so these share one toast (USE-2767).
  if (
    error instanceof BedrockApiRequestError &&
    error.code === REALTIME_RPC_REJECTED_CODE
  ) {
    return {
      title: GENERIC_MINIAPP_ERROR_TITLE,
      id: "miniapp-rpc-error",
      cause: { errorCode: error.code },
    };
  }
  if (error instanceof BedrockApiRequestError) {
    const reploErrorId = error.reploErrorId;
    // NOTE (Gabe, 2026-09-23): the client appends the id to internal errors; a per-incident title never groups.
    const idSuffix = reploErrorId ? ` (error id ${reploErrorId})` : "";
    const message =
      idSuffix && error.message.endsWith(idSuffix)
        ? error.message.slice(0, -idSuffix.length)
        : error.message;
    const title = message.length > 0 ? message : GENERIC_MINIAPP_ERROR_TITLE;
    return {
      title,
      // Every query behind one outage (a sandbox that never became ready) fails
      // with the same message; one id collapses that burst into one toast.
      id: `miniapp-api-error:${error.code}:${title}`,
      ...(message === error.message
        ? {}
        : { description: `Error ID: ${reploErrorId}` }),
      cause: {
        errorCode: error.code,
        ...(reploErrorId ? { errorId: reploErrorId } : {}),
      },
    };
  }
  if (error instanceof UserFacingError) {
    return {
      title: error.message,
      ...(error.description === null ? {} : { description: error.description }),
      id: `miniapp-user-facing-error:${error.message}`,
    };
  }
  const cause = getUnexpectedErrorCause(error);
  return {
    title: GENERIC_MINIAPP_ERROR_TITLE,
    // A failed connection rejects every waiting request with one stray error; one id per cause collapses that burst.
    id: `miniapp-unexpected-error:${cause.errorName}:${cause.errorMessage ?? ""}`,
    cause,
  };
}

function getUnexpectedErrorCause(error: unknown): {
  errorName: string;
  errorMessage?: string;
} {
  if (error instanceof Error) {
    return { errorName: error.name, errorMessage: error.message };
  }
  const text = z.string().safeParse(error);
  if (text.success) {
    return { errorName: "string", errorMessage: text.data };
  }
  return { errorName: typeof error };
}

/**
 * Single-line, user-facing reason for an inline failure state (e.g. a failed
 * upload tile), the counterpart of `getMiniappErrorToastContent` for surfaces
 * that show one string rather than a toast. A failed session-token mint becomes
 * a human reason instead of the raw `requestAuth` plumbing (REPL-31464); a
 * bedrock-api `BedrockApiRequestError` keeps its (user-facing) server message,
 * and a `UserFacingError` keeps its message; everything else — coordinator
 * rejections, other internal RPC failures, stray JS errors, non-errors —
 * returns `fallback`, so no raw/internal string is ever surfaced.
 */
export function getMiniappErrorMessage(
  error: unknown,
  fallback: string = GENERIC_MINIAPP_ERROR_TITLE,
): string {
  if (isMiniappAuthTokenError(error)) {
    return "Couldn't verify your session. Try again in a moment.";
  }
  if (error instanceof UserFacingError) {
    return error.message;
  }
  if (
    error instanceof BedrockApiRequestError &&
    error.code !== REALTIME_RPC_REJECTED_CODE &&
    error.message.length > 0
  ) {
    return error.message;
  }
  return fallback;
}
